import React, { useEffect, useCallback } from 'react';
import TutorMatchCard from './TutorMatchCard';
import MatchProgressIndicator from './MatchProgressIndicator';
import { getStudentDisplayName } from '../../utils/studentName';

/**
 * TutorMatchPanel
 *
 * Slide-in side panel for displaying AI-powered tutor match results.
 * Shows streaming progress during matching, then displays top 5 matches
 * with the ability to assign a tutor directly.
 */
const TutorMatchPanel = ({
  student,
  matches = [],
  isStreaming = false,
  progress = { message: '' },
  error = null,
  onAssign,
  onClose,
  isAssigning = false,
  assigningTutorId = null
}) => {
  // Handle escape key to close panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle overlay click to close
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Get student display name
  const getStudentName = () => {
    return getStudentDisplayName(student);
  };

  // Parse student subjects
  const getSubjects = () => {
    if (!student?.target_subjects) return [];
    return student.target_subjects.split(',').map(s => s.trim()).slice(0, 3);
  };

  if (!student) return null;

  return (
    <div className="tutor-match-overlay" onClick={handleOverlayClick}>
      <div className="tutor-match-panel" role="dialog" aria-modal="true">
        {/* Header */}
        <header className="tutor-match-panel__header">
          <div className="tutor-match-panel__header-content">
            <div className="tutor-match-panel__header-icon">
              <img src="/lrbrain-logo.png" alt="" />
            </div>
            <div className="tutor-match-panel__header-text">
              <h2 className="tutor-match-panel__title">Find Tutor</h2>
              <p className="tutor-match-panel__subtitle">{getStudentName()}</p>
            </div>
          </div>
          <button
            className="tutor-match-panel__close"
            onClick={onClose}
            aria-label="Close panel"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </header>

        {/* Student Info Summary */}
        <div className="tutor-match-panel__student-summary">
          <div className="tutor-match-panel__student-detail">
            <span className="tutor-match-panel__label">Grade</span>
            <span className="tutor-match-panel__value">
              {student.grade_year_level || '-'}
            </span>
          </div>
          {getSubjects().length > 0 && (
            <div className="tutor-match-panel__student-detail">
              <span className="tutor-match-panel__label">Subjects</span>
              <div className="tutor-match-panel__subjects">
                {getSubjects().map((subject, idx) => (
                  <span key={idx} className="tutor-match-panel__subject-badge">
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="tutor-match-panel__content">
          {/* Loading/Streaming State */}
          {isStreaming && (
            <MatchProgressIndicator
              message={progress.message || 'Analyzing student profile...'}
              isActive={true}
            />
          )}

          {/* Error State */}
          {error && !isStreaming && (
            <div className="tutor-match-panel__error">
              <div className="tutor-match-panel__error-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Unable to find matches</h3>
              <p>{error}</p>
              <button
                className="tutor-match-panel__retry-btn"
                onClick={() => window.location.reload()}
                type="button"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Match Results */}
          {!isStreaming && !error && matches.length > 0 && (
            <div className="tutor-match-panel__results">
              <div className="tutor-match-panel__results-header">
                <h3>Top {matches.length} Matches</h3>
                <p>Select a tutor to assign to this student</p>
              </div>
              <div className="tutor-match-panel__matches-list">
                {matches.map((match, idx) => (
                  <TutorMatchCard
                    key={match.tutor_id}
                    match={match}
                    rank={idx + 1}
                    onAssign={onAssign}
                    isAssigning={isAssigning}
                    isAssigningThisTutor={assigningTutorId === match.tutor_id}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No Matches State */}
          {!isStreaming && !error && matches.length === 0 && (
            <div className="tutor-match-panel__empty">
              <div className="tutor-match-panel__empty-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                  <path d="M17 24H31M24 17V31" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>No matches found</h3>
              <p>We couldn't find suitable tutors for this student's profile. Try adjusting their learning preferences or check tutor availability.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorMatchPanel;
